import React, { useState } from 'react';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  eachDayOfInterval, 
  isSameMonth, 
  isSameDay, 
  addMonths, 
  subMonths,
  isToday
} from 'date-fns';
import SafeIcon from '../../common/SafeIcon';
import { FiChevronLeft, FiChevronRight, FiCalendar, FiClock } from 'react-icons/fi';

const CalendarView = ({ products }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const days = eachDayOfInterval({
    start: startOfWeek(startOfMonth(currentMonth)),
    end: endOfWeek(endOfMonth(currentMonth)),
  });

  const getProductsForDay = (day) => {
    return products.filter(p => p.targetDate && isSameDay(new Date(p.targetDate), day));
  };

  return (
    <div className="bg-white rounded-3xl border border-cream-dark shadow-sm overflow-hidden animate-fade-in">
      <div className="p-6 border-b border-cream-dark flex items-center justify-between bg-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-terracotta/10 flex items-center justify-center">
            <SafeIcon icon={FiCalendar} className="text-terracotta w-5 h-5" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-espresso">{format(currentMonth, 'MMMM yyyy')}</h3>
            <p className="text-[10px] text-espresso-light/60 uppercase tracking-widest font-bold">Production Deadlines</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            className="p-2 hover:bg-cream rounded-lg text-espresso-light transition-colors"
          >
            <SafeIcon icon={FiChevronLeft} />
          </button>
          <button 
            onClick={() => setCurrentMonth(new Date())}
            className="px-3 py-1 text-xs font-bold text-espresso-light hover:bg-cream rounded-lg border border-cream-dark transition-colors"
          >
            Today
          </button>
          <button 
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            className="p-2 hover:bg-cream rounded-lg text-espresso-light transition-colors"
          >
            <SafeIcon icon={FiChevronRight} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 bg-cream/30 border-b border-cream-dark">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="py-3 text-center text-[10px] font-bold text-espresso-light/40 uppercase tracking-tighter">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 auto-rows-[minmax(120px,1fr)]">
        {days.map((day, idx) => {
          const dayProducts = getProductsForDay(day);
          const isCurrentMonth = isSameMonth(day, currentMonth);
          
          return (
            <div 
              key={day.toString()} 
              className={`p-2 border-r border-b border-cream-dark min-h-[120px] transition-colors ${
                !isCurrentMonth ? 'bg-cream/10' : 'bg-white'
              } ${isToday(day) ? 'bg-terracotta/5' : ''}`}
            >
              <div className="flex justify-between items-start mb-2">
                <span className={`text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full ${
                  isToday(day) ? 'bg-terracotta text-white' : isCurrentMonth ? 'text-espresso' : 'text-espresso-light/20'
                }`}>
                  {format(day, 'd')}
                </span>
                {dayProducts.length > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-sage/10 text-sage rounded font-bold">
                    {dayProducts.length}
                  </span>
                )}
              </div>
              
              <div className="space-y-1">
                {dayProducts.slice(0, 3).map(product => (
                  <div 
                    key={product.id}
                    className="text-[10px] p-1.5 rounded-md bg-white border border-cream-dark shadow-sm truncate font-medium text-espresso hover:border-terracotta/40 transition-colors cursor-default"
                    title={product.title}
                  >
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-terracotta mr-1.5"></span>
                    {product.title}
                  </div>
                ))}
                {dayProducts.length > 3 && (
                  <div className="text-[9px] text-espresso-light/40 font-bold px-1">
                    + {dayProducts.length - 3} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CalendarView;